import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../store/store";

// DOM id of the navbar launcher button — shared so the floating widget can
// locate it to animate to/from the navbar.
export const CHATBOT_LAUNCHER_ID = "chatbot-navbar-launcher";

// localStorage key persisting the hidden state, so closing the chatbot
// sticks across tabs and reloads.
const CHATBOT_HIDDEN_STORAGE_KEY = "chatBotHidden";

const readPersistedHidden = (): boolean => {
  try {
    return localStorage.getItem(CHATBOT_HIDDEN_STORAGE_KEY) === "true";
  } catch (e) {
    return false;
  }
};

const persistHidden = (isHidden: boolean) => {
  try {
    localStorage.setItem(CHATBOT_HIDDEN_STORAGE_KEY, String(isHidden));
  } catch (e) {
    // Storage unavailable — visibility just won't persist.
  }
};

export interface LauncherRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface ChatBotState {
  // When true, the floating chatbot is hidden and a launcher icon is shown in the navbar.
  isHidden: boolean;
  // Screen position of the navbar launcher icon, used to animate the widget to/from the navbar.
  launcherRect: LauncherRect | null;
}

const initialState: ChatBotState = {
  isHidden: readPersistedHidden(),
  launcherRect: null,
};

const chatBotSlice = createSlice({
  name: "chatBot",
  initialState,
  reducers: {
    hideChatBot(state) {
      state.isHidden = true;
      persistHidden(true);
    },
    showChatBot(state) {
      state.isHidden = false;
      persistHidden(false);
    },
    toggleChatBot(state) {
      state.isHidden = !state.isHidden;
      persistHidden(state.isHidden);
    },
    setLauncherRect(state, action: PayloadAction<LauncherRect>) {
      state.launcherRect = action.payload;
    },
  },
});

export const selectChatBotHidden = (state: RootState) =>
  state.chatBot?.isHidden ?? false;
export const selectLauncherRect = (state: RootState) =>
  state.chatBot?.launcherRect ?? null;
export const { hideChatBot, showChatBot, toggleChatBot, setLauncherRect } =
  chatBotSlice.actions;
export default chatBotSlice.reducer;